import type { ToolState } from '../types/LabelingTypes';
import type { DichotomousState } from './dichotomousSearch';

/**
 * Step 3 of the spec: verification sampling and the zone expectation the answers are checked
 * against. Pure and framework-free, like its `dichotomousSearch.ts` sibling.
 *
 * Only *types* are imported from `dichotomousSearch.ts` (erased at build time), while that module
 * imports `beginVerification`/`expectedLabel` from here at runtime — so the dependency is a single
 * runtime edge, not a cycle.
 */

/** Never ask more than this many verification questions, however large the sequence is. */
const MAX_VERIFICATION_SAMPLE = 5;

/**
 * The label a converged bracket predicts for `index`: everything up to and including `lo` is ok,
 * everything from `hi` on is worn. This is the *only* definition of a verification expectation —
 * `answerDichotomous`'s `'verify'` branch and the conflict dialog copy both read it from here, so
 * the annotator can never be shown an expectation the state machine did not actually test against.
 */
export function expectedLabel(state: DichotomousState, index: number): ToolState {
  return index <= state.lo ? 'ok' : 'worn';
}

/**
 * Picks the images to double-check once the search has converged.
 *
 * Candidates are the indices with **no recorded answer** — which excludes the two endpoints, the
 * converged `lo`/`hi` pair and every midpoint probed on the way down, so the annotator is never
 * asked the same image twice. The sample size is `min(5, candidates.length)`: the spec's literal
 * `min(5, n - 4)` assumes exactly 4 answered images, but step 2 answers `⌈log₂ n⌉` of them, so
 * `n - 4` overcounts the pool and would ask for images that do not exist.
 *
 * When both zones still hold candidates, one of each is drawn first — a sample taken entirely
 * from one side of the breakpoint would not test the breakpoint at all. When a zone is exhausted
 * (e.g. a break at the very last image leaves nothing unanswered on the worn side), the sample is
 * simply filled from the other zone rather than forcing an impossible guarantee.
 *
 * All randomness goes through the injected `rand`, so a caller can make a run reproducible.
 * The result is ascending, and `state` is never touched.
 */
export function sampleVerificationIndices(
  state: DichotomousState,
  rand: () => number = Math.random
): number[] {
  const unanswered = state.keys
    .map((_, index) => index)
    .filter((index) => !(index in state.answers));

  const okZone = unanswered.filter((index) => index <= state.lo);
  const wornZone = unanswered.filter((index) => index >= state.hi);
  // In a converged bracket (`hi - lo === 1`) the two zones cover every candidate. Called on a
  // state that has not converged yet, the indices inside the still-unknown `(lo, hi)` window have
  // no expected label, so they are not offered for verification.
  const pool = [...okZone, ...wornZone];

  const size = Math.min(MAX_VERIFICATION_SAMPLE, pool.length);
  if (size === 0) return [];

  const picked = new Set<number>();
  const drawFrom = (source: number[]): void => {
    const available = source.filter((index) => !picked.has(index));
    if (available.length === 0) return;
    // Guard against a `rand` that returns exactly 1 (or more) — an out-of-range draw would
    // silently pick `undefined` and corrupt the queue.
    const position = Math.min(
      available.length - 1,
      Math.max(0, Math.floor(rand() * available.length))
    );
    picked.add(available[position]);
  };

  if (size >= 2 && okZone.length > 0 && wornZone.length > 0) {
    drawFrom(okZone);
    drawFrom(wornZone);
  }
  // `pool` minus `picked` is non-empty as long as `picked.size < size <= pool.length`, so each
  // pass adds exactly one index and the loop terminates.
  while (picked.size < size) {
    drawFrom(pool);
  }

  return [...picked].sort((a, b) => a - b);
}

/**
 * Draws one more image to verify after the one on screen turned out unreadable — an unreadable
 * image tests nothing, so it must not count towards the sample. Candidates are, as in
 * `sampleVerificationIndices`, the indices with no recorded answer, minus the ones already
 * queued. The replaced image's `zone` is preferred, so the sample keeps covering both sides of
 * the breakpoint; the other zone is the fallback. `null` when nothing is left to draw.
 *
 * `state` is the state *after* the unreadable image was removed and every index shifted.
 */
export function drawVerificationReplacement(
  state: DichotomousState,
  zone: ToolState,
  rand: () => number = Math.random
): number | null {
  const candidates = state.keys
    .map((_, index) => index)
    .filter((index) => !(index in state.answers) && !state.verifyQueue.includes(index));
  const sameZone = candidates.filter((index) => expectedLabel(state, index) === zone);
  const source = sameZone.length > 0 ? sameZone : candidates;
  if (source.length === 0) return null;

  // Same out-of-range guard as `sampleVerificationIndices`' draw.
  const position = Math.min(source.length - 1, Math.max(0, Math.floor(rand() * source.length)));
  return source[position];
}

/**
 * Moves a converged search into step 3.
 *
 * With no candidate left (n === 2, where both images are endpoints, or any run that happened to
 * probe every index) verification would be a no-op, so the state goes straight to `'summary'`.
 * Otherwise the sample *is* the work queue, and `verifyTotal` freezes its initial length: the
 * "Verification N of total" line must count up against a fixed denominator, not one that shrinks
 * as the queue drains.
 */
export function beginVerification(
  state: DichotomousState,
  rand: () => number = Math.random
): DichotomousState {
  const verifyQueue = sampleVerificationIndices(state, rand);

  return {
    ...state,
    phase: verifyQueue.length === 0 ? 'summary' : 'verify',
    verifyQueue,
    verifyTotal: verifyQueue.length,
    // A re-run after a conflict comes back through here; the previous conflict is resolved by then.
    conflict: null,
  };
}
